import { useState, useEffect, useCallback, useRef } from 'react';

export interface TypewriterOptions {
  speed?: number; // Speed in milliseconds per character
  onComplete?: () => void; // Callback when typing is finished
  enabled?: boolean; // Whether typing is enabled
}

export interface TypewriterResult {
  displayedText: string;
  isTyping: boolean;
  skipTyping: () => void;
}

/**
 * useTypewriter
 * 
 * A custom hook that reveals text character-by-character.
 * 
 * @param fullText - The full text to be displayed
 * @param options - Optional configuration (speed, onComplete callback, enabled flag)
 * @returns Object containing the current displayed text, typing status, and a skip function
 */
export const useTypewriter = (
  fullText: string,
  options: TypewriterOptions = {}
): TypewriterResult => {
  const { speed = 40, onComplete, enabled = true } = options;
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Use a ref for onComplete to avoid effect re-runs if the callback changes
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Use a ref for the interval to allow cleanup and skipping
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const skipTyping = useCallback(() => {
    cleanup();
    setDisplayedText(fullText);
    setIsTyping(false);
    
    if (onCompleteRef.current) {
      onCompleteRef.current();
    }
  }, [fullText, cleanup]);

  useEffect(() => {
    cleanup();

    if (!enabled) {
      setDisplayedText(fullText);
      setIsTyping(false);
      return;
    }

    // Reset state when fullText changes
    setDisplayedText('');
    
    if (!fullText) {
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    let currentIndex = 0;

    intervalRef.current = setInterval(() => {
      currentIndex++;
      
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndex));
      } else {
        cleanup();
        setIsTyping(false);
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }
    }, speed);

    return cleanup;
  }, [fullText, speed, enabled, cleanup]);

  return {
    displayedText,
    isTyping,
    skipTyping
  };
};
